import { MediaFragment } from 'generated/graphql';
import type { SetDose } from '@client/new/components/SetDoseCard/SetDoseCard';

export const MAX_SWAP_PROPOSALS = 5;

// One dosage set of a type-2 exercise: `[amount, unit]` plus its set mode.
export type SwapSet = SetDose;

export const copySwapSet = ({ reps, mode, repsMax }: SwapSet): SwapSet => ({
  reps: [...reps],
  ...(mode === undefined ? {} : { mode }),
  ...(repsMax == null ? {} : { repsMax }),
});

export type SwapProposal = {
  name: string;
  description?: string | null;
  media?: MediaFragment | null;
  sets: SwapSet[];
};

export type ExerciseSwapConfig = {
  swapsEnabled: boolean;
  proposals: SwapProposal[];
};

// UI-local exercise identity (search candidate filtering, proposal
// tracking). Slot matching and write dedupe live in creator-api, which
// mirrors the engine's normalization - this stays consistent with both.
export const exerciseNameKey = (name: string): string =>
  name.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

export const defaultSwapConfig = (): ExerciseSwapConfig => ({
  swapsEnabled: true,
  proposals: [],
});

export const proposalFromCatalog = (
  catalogExercise: {
    name: string;
    description?: string | null;
    media?: MediaFragment | null;
  },
  originalSets: SwapSet[] | undefined,
): SwapProposal => ({
  name: catalogExercise.name,
  description: catalogExercise.description,
  media: catalogExercise.media,
  sets: (originalSets ?? []).map(copySwapSet),
});

const hasValidRangeBound = (set: SwapSet): boolean =>
  set.mode !== 'range' || Number(set.repsMax) > Number(set.reps[0]);

export const isValidSet = (set: SwapSet): boolean => {
  const [amount, unit] = set.reps;
  const amountText = `${amount ?? ''}`.trim();
  return (
    amountText !== '' &&
    !Number.isNaN(Number(amountText)) &&
    typeof unit === 'string' &&
    unit.length > 0 &&
    hasValidRangeBound(set)
  );
};

// A blank upper bound only disables Save; the message waits for a typed one.
export const rangeBoundError = (set: SwapSet): string | undefined =>
  set.repsMax == null || `${set.repsMax}` === '' || hasValidRangeBound(set)
    ? undefined
    : 'Upper bound must be greater than the amount';

const setLabel = ({ reps: [amount, unit], mode, repsMax }: SwapSet): string => {
  switch (mode) {
    case 'toFailure':
      return 'To failure';
    case 'range':
      return `${amount}-${repsMax} ${unit}`;
    case 'minimum':
      return `${amount}+ ${unit}`;
    default:
      return `${amount} ${unit}`;
  }
};

export const doseLabel = (sets: SwapSet[]): string => {
  if (sets.length === 0) return 'No sets';
  const labels = sets.map(setLabel);
  return sets.length > 1 && labels.every((label) => label === labels[0])
    ? `${sets.length} x ${labels[0]}`
    : labels.join(', ');
};
